// src/components/LexingtonAnimation.js
import React, { useState, useEffect } from "react";

// Phase 1: Background moving behind a stationary horse.
const phase1Frames = [
  "🏇🟦🟦🟦🟦🌳🟦",
  "🏇🟦🟦🟦🌳🟦🟦",
  "🏇🟦🟦🌳🟦🟦🟦",
  "🏇🟦🌳🟦🟦🟦🟦",
  "🏇🌳🟦🟦🟦🟦🌳",
  "🏇🟦🟦🟦🟦🌳🟦",
  "🏇🟦🟦🟦🌳🟦🟦",
  "🏇🟦🟦🌳🟦🟦🟦",
  "🏇🟦🌳🟦🟦🟦🟦",
  "🏇🌳🟦🟦🟦🟦🟦",
];

// Phase 2: Finish-line phase.
const phase2Frames = [
  "🏇🟦🟦🟦🟦🟦🏁",
  "🟦🏇🟦🟦🟦🟦🏁",
  "🟦🟦🏇🟦🟦🟦🏁",
  "🟦🟦🟦🏇🟦🟦🏁",
  "🟦🟦🟦🟦🏇🟦🏁",
  "🟦🟦🟦🟦🟦🏇🏁",
  "🟦🟦🟦🟦🟦🟦🏇",
];

// Phase 3: Celebration.
const phase3Frames = [
  "🏆🎉🎉🎉🏆",
  "🎉🏆🏆🏆🎉",
  "🏆🎉🎉🎉🏆",
  "🎉🏆🏆🏆🎉",
  "🏆🎉🎉🎉🏆",
  "🎉🏆🏆🏆🎉",
  "🏆🎉🎉🎉🏆",
  "🎉🏆🏆🏆🎉",
  "🏆🎉🎉🎉🏆",
  "🎉🏆🏆🏆🎉",
];

const FRAME_INTERVAL = 200; // 200ms per frame (triple the speed)

const LexingtonAnimation = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [phase, setPhase] = useState(1);
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    let interval;
    if (isHovered) {
      interval = setInterval(() => {
        setFrameIndex((prev) => {
          let newIndex = prev + 1;
          if (phase === 1 && newIndex >= phase1Frames.length) {
            setPhase(2);
            newIndex = 0;
          } else if (phase === 2 && newIndex >= phase2Frames.length) {
            setPhase(3);
            newIndex = 0;
          } else if (phase === 3 && newIndex >= phase3Frames.length) {
            // Reset animation after celebration.
            setIsHovered(false);
            setPhase(1);
            newIndex = 0;
          }
          return newIndex;
        });
      }, FRAME_INTERVAL);
    }
    return () => clearInterval(interval);
  }, [isHovered, phase]);

  // Determine the current text.
  let currentText;
  if (!isHovered) {
    currentText = "Based in Lexington, Kentucky";
  } else if (phase === 1) {
    currentText = phase1Frames[frameIndex];
  } else if (phase === 2) {
    currentText = phase2Frames[frameIndex];
  } else if (phase === 3) {
    currentText = phase3Frames[frameIndex];
  }

  // Wrap every horse emoji with a span to flip it.
  const renderWithFlippedHorse = (text) => {
    const parts = text.split("🏇");
    return parts.reduce((acc, part, index) => {
      if (index === 0) return [part];
      return [
        ...acc,
        <span
          key={index}
          style={{ display: "inline-block", transform: "scaleX(-1)" }}
        >
          🏇
        </span>,
        part,
      ];
    }, []);
  };

  return (
    <div
      // Here we set a fixed width that matches (or exceeds) the natural width of 
      // "Based in Lexington, Kentucky" so that the space is reserved regardless of content.
      style={{
        width: "200px",      // Adjust this value to match your layout.
        textAlign: "center", // Center the text inside the reserved space.
        whiteSpace: "nowrap",
      }}
      className="inline-block text-[#e0dadd] cursor-pointer select-none"
      onMouseEnter={() => {
        setIsHovered(true);
        setPhase(1);
        setFrameIndex(0);
      }}
      onMouseLeave={() => {
        // Reset immediately on mouse leave.
        setIsHovered(false);
        setPhase(1);
        setFrameIndex(0);
      }}
    >
      {renderWithFlippedHorse(currentText)}
    </div>
  );
};

export default LexingtonAnimation;
